// Cliente de Cognito del pool administrativo (amazon-cognito-identity-js,
// flujo USER_SRP). Mudado desde apps/tower/src/auth/cognito.ts: lo unico
// nuevo es que el emisor TOTP y el nombre del dispositivo los elige cada SPA
// (la torre conserva exactamente los suyos, ver apps/tower/src/auth/cognito.ts).
//
// Los usuarios los crea un admin, asi que el primer login recorre
// NEW_PASSWORD_REQUIRED y despues el TOTP. Con el pool en REQUIRED, Cognito
// pide MFA_SETUP (asociar software token) antes de la sesion; los logins
// siguientes, SOFTWARE_TOKEN_MFA (codigo de 6 digitos).
//
// ADR-024: con el pool en OPTIONAL, Cognito entrega la sesion sin TOTP a una
// cuenta que no lo tiene. Si la SPA lo exige (torre, consola) y el ID token
// dice `ucorp_mfa: none`, la sesion no se entrega: se enrola el TOTP con esa
// sesion, se descarta y se pide un login nuevo, que ya pasa por el desafio.
// La API es la que decide (plugins/admin-mfa.ts); esto evita que el usuario
// llegue a una pantalla que no puede usar.
//
// La libreria persiste tokens en el storage que le demos: le pasamos uno en
// memoria para que NO toque localStorage; la persistencia de la sesion la
// maneja session.ts (memoria + sessionStorage).
import { AuthenticationDetails, CognitoRefreshToken, CognitoUser, CognitoUserPool, } from "amazon-cognito-identity-js";
/** Torre y consola: TOTP obligatorio. Es el valor por defecto (falla cerrada). */
export const TOTP_OBLIGATORIO = { exigeTotp: true };
/** Claim del ID token que escribe el trigger del pool administrativo (ADR-024). */
export const CLAIM_MFA = "ucorp_mfa";
/**
 * Lo que dice el ID token sobre el TOTP de la cuenta. `undefined` si el token
 * no trae el claim (pool en REQUIRED sin trigger todavia): ahi Cognito ya
 * desafio y la SPA no agrega nada.
 */
export function evidenciaMfa(payload) {
    const value = payload[CLAIM_MFA];
    return value === "totp" || value === "none" || value === "unverified" ? value : undefined;
}
/**
 * Que hace la SPA con una sesion recien emitida: entregarla, enrolar el TOTP
 * o rechazarla (Cognito no pudo confirmar el TOTP de la cuenta).
 */
export function decisionMfa(politica, evidencia) {
    if (!politica.exigeTotp || evidencia === "totp" || evidencia === undefined)
        return "sesion";
    return evidencia === "none" ? "enrolar" : "rechazar";
}
export const MENSAJE_MFA_NO_VERIFICADO = "No pudimos confirmar tu segundo factor. Probá de nuevo en unos minutos.";
/** URL otpauth:// estandar para cargar el secreto en la app de autenticacion. */
export function urlOtpauth(secreto, email, issuer) {
    const label = `${encodeURIComponent(issuer)}:${encodeURIComponent(email)}`;
    return `otpauth://totp/${label}?secret=${secreto}&issuer=${encodeURIComponent(issuer)}`;
}
/** Storage en memoria: evita que la libreria escriba tokens en localStorage. */
class StorageEnMemoria {
    data = new Map();
    setItem(key, value) {
        this.data.set(key, value);
    }
    getItem(key) {
        return this.data.get(key) ?? null;
    }
    removeItem(key) {
        this.data.delete(key);
    }
    clear() {
        this.data.clear();
    }
}
function aSesion(session, user) {
    // ID token, no access: los atributos custom viajan solo en el ID token.
    const token = session.getIdToken();
    // El refresh token permite renovar sin volver al login (turno entero).
    const refreshToken = session.getRefreshToken()?.getToken();
    return {
        accessToken: token.getJwtToken(),
        expiraEn: token.getExpiration(),
        user,
        ...(refreshToken !== undefined && refreshToken !== ""
            ? { refreshToken }
            : {}),
    };
}
/**
 * Renueva la sesion con el refresh token de Cognito (CognitoUser.refreshSession).
 * Devuelve una WebSession nueva (Cognito conserva el mismo refresh token; si
 * la respuesta no lo trae, se arrastra el vigente). Rechaza si la sesion no
 * tiene refresh token (sesion guardada vieja) o si Cognito lo rechaza
 * (revocado o vencido): en ese caso el caller cierra la sesion.
 */
export function renovarSesion(config, session, politica = TOTP_OBLIGATORIO) {
    const refreshToken = session.refreshToken;
    if (refreshToken === undefined) {
        return Promise.reject(new Error("La sesion no tiene refresh token: hay que volver a iniciar sesion."));
    }
    const pool = new CognitoUserPool({
        UserPoolId: config.userPoolId,
        ClientId: config.clientId,
        Storage: new StorageEnMemoria(),
    });
    const user = new CognitoUser({
        Username: session.user,
        Pool: pool,
        Storage: new StorageEnMemoria(),
    });
    return new Promise((resolve, reject) => {
        user.refreshSession(new CognitoRefreshToken({ RefreshToken: refreshToken }), (error, next) => {
            if (error !== null) {
                reject(error);
                return;
            }
            // ADR-024: la renovacion vuelve a pasar por el trigger. Si la cuenta
            // ya no tiene TOTP (lo desactivo), la sesion no se renueva.
            if (decisionMfa(politica, evidenciaMfa(next.getIdToken().decodePayload())) !== "sesion") {
                reject(new Error("La cuenta ya no tiene segundo factor: hay que volver a iniciar sesion."));
                return;
            }
            const renovada = aSesion(next, session.user);
            resolve(renovada.refreshToken !== undefined
                ? renovada
                : { ...renovada, refreshToken });
        });
    });
}
/**
 * Descarta una sesion sin TOTP: revoca su refresh token en Cognito y limpia
 * la libreria. Si la revocacion falla, igual se limpia; esa sesion nunca
 * llego a la app y sus tokens no pasan la API (ADR-024).
 */
function descartarSesion(user) {
    user.signOut(() => {
        user.signOut();
    });
}
export class ClienteCognito {
    pool;
    branding;
    politica;
    user = null;
    email = "";
    constructor(config, branding, politica = TOTP_OBLIGATORIO) {
        this.branding = branding;
        this.politica = politica;
        this.pool = new CognitoUserPool({
            UserPoolId: config.userPoolId,
            ClientId: config.clientId,
            Storage: new StorageEnMemoria(),
        });
    }
    /** Callbacks comunes a todos los pasos: cada challenge resuelve un PasoAuth. */
    callbacks(resolve, reject) {
        return {
            onSuccess: (session) => {
                this.entregar(session, resolve, reject);
            },
            onFailure: (error) => {
                reject(error);
            },
            newPasswordRequired: () => {
                resolve({ type: "nueva_contrasena" });
            },
            mfaSetup: () => {
                const user = this.user;
                if (user === null) {
                    reject(new Error("Flujo de login inconsistente: no hay usuario activo."));
                    return;
                }
                user.associateSoftwareToken({
                    associateSecretCode: (secreto) => {
                        resolve({
                            type: "mfa_setup",
                            secreto,
                            urlOtpauth: urlOtpauth(secreto, this.email, this.branding.issuer),
                        });
                    },
                    onFailure: (error) => {
                        reject(error);
                    },
                });
            },
            totpRequired: () => {
                resolve({ type: "mfa_codigo" });
            },
        };
    }
    /**
     * Entrega la sesion o, si la SPA exige TOTP y la cuenta no lo tiene, la
     * retiene y arranca el enrolamiento (ADR-024). Una sesion retenida nunca
     * llega a la app ni al storage.
     */
    entregar(session, resolve, reject) {
        const decision = decisionMfa(this.politica, evidenciaMfa(session.getIdToken().decodePayload()));
        if (decision === "sesion") {
            resolve({ type: "sesion", session: aSesion(session, this.email) });
            return;
        }
        const user = this.user;
        if (decision === "rechazar" || user === null) {
            if (user !== null)
                descartarSesion(user);
            reject(new Error(MENSAJE_MFA_NO_VERIFICADO));
            return;
        }
        // Sesion autenticada sin TOTP: la libreria asocia el token con su access token.
        user.associateSoftwareToken({
            associateSecretCode: (secreto) => {
                resolve({
                    type: "mfa_enrolamiento",
                    secreto,
                    urlOtpauth: urlOtpauth(secreto, this.email, this.branding.issuer),
                });
            },
            onFailure: (error) => {
                descartarSesion(user);
                reject(error);
            },
        });
    }
    /**
     * ADR-024: confirma el TOTP recien asociado, lo deja habilitado y preferido
     * (asi Cognito lo pide en cada login) y descarta la sesion sin TOTP: el
     * usuario vuelve a entrar y esta vez pasa por el desafio.
     */
    confirmarEnrolamiento(codigo) {
        return new Promise((resolve, reject) => {
            const user = this.user;
            if (user === null) {
                reject(new Error("No hay login en curso: volve a ingresar email y contrasena."));
                return;
            }
            user.verifySoftwareToken(codigo, this.branding.deviceName, {
                onSuccess: () => {
                    user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (error) => {
                        if (error !== undefined && error !== null) {
                            reject(error);
                            return;
                        }
                        descartarSesion(user);
                        this.user = null;
                        resolve({ type: "reingresar" });
                    });
                },
                onFailure: (error) => {
                    reject(error);
                },
            });
        });
    }
    /** Paso 1: email + contrasena por SRP. */
    iniciarSesion(email, contrasena) {
        this.email = email;
        this.user = new CognitoUser({
            Username: email,
            Pool: this.pool,
            Storage: new StorageEnMemoria(),
        });
        const detalles = new AuthenticationDetails({ Username: email, Password: contrasena });
        return new Promise((resolve, reject) => {
            this.user?.authenticateUser(detalles, this.callbacks(resolve, reject));
        });
    }
    /** Challenge NEW_PASSWORD_REQUIRED (usuario recien creado por admin). */
    completarNuevaContrasena(nuevaContrasena) {
        return new Promise((resolve, reject) => {
            const user = this.user;
            if (user === null) {
                reject(new Error("No hay login en curso: volve a ingresar email y contrasena."));
                return;
            }
            user.completeNewPasswordChallenge(nuevaContrasena, {}, this.callbacks(resolve, reject));
        });
    }
    /** Challenge MFA_SETUP: verificar el primer codigo del token recien asociado. */
    verificarTotpInicial(codigo) {
        return new Promise((resolve, reject) => {
            const user = this.user;
            if (user === null) {
                reject(new Error("No hay login en curso: volve a ingresar email y contrasena."));
                return;
            }
            user.verifySoftwareToken(codigo, this.branding.deviceName, {
                onSuccess: (session) => {
                    this.entregar(session, resolve, reject);
                },
                onFailure: (error) => {
                    reject(error);
                },
            });
        });
    }
    /** Challenge SOFTWARE_TOKEN_MFA: codigo de 6 digitos de logins siguientes. */
    responderCodigoMfa(codigo) {
        return new Promise((resolve, reject) => {
            const user = this.user;
            if (user === null) {
                reject(new Error("No hay login en curso: volve a ingresar email y contrasena."));
                return;
            }
            user.sendMFACode(codigo, this.callbacks(resolve, reject), "SOFTWARE_TOKEN_MFA");
        });
    }
}
//# sourceMappingURL=cognito.js.map